import { influencer } from '@round/api';
import { HeaderContext as LibHeaderContext } from '@tanstack/react-table';
import { getTableMetaHelper } from '@round/ui-kit';
import { tableHelpers } from '@round/creatorbase-ui';
import cn from 'classnames';
import { CreatorCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/CreatorCell/CreatorCell';
import { LocationCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/LocationCell/LocationCell';
import { ResponseRateCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/ResponseRateCell/ResponseRateCell';
import { TagsCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/TagsCell/TagsCell';
import {
    AvgCpeCell,
    CostCell,
    CpmCell,
    EnagementRateCell,
    FollowersCell,
    PlaysCell,
    PostingFrequencyCell,
} from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/PlatformStatCells/PlatformStatCells';
import { PlatformSelectorCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/PlatformSelectorCell/PlatformSelectorCell';
import {
    CellContext,
    Column,
    Meta as BaseMeta,
    DATE_ADDED_COLUMN_ID,
    getDateAddedValue,
} from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { getAverageDraftTimeLabel, getLastCollabLabel } from 'Modules/Plans/CreatorSearch/utils/creatorSearchUtils';
import { CreatorPoolAccounts } from '../../helpers';
import styles from './AddCreatorsTable.module.css';

const SkeletonCell = tableHelpers.SkeletonCell;

const PLATFORM_STAT_COLUMN_SIZE = 115;

export const ROW_HEIGHT_PX = 50;

export type HeaderContext = LibHeaderContext<CellContext['row']['original'], unknown>;

export type Meta = Pick<
    BaseMeta,
    | 'isLoading'
    | 'hasError'
    | 'getCreatorImages'
    | 'getAreCreatorImagesLoading'
    | 'getRowSelectedPlatform'
    | 'onRowPlatformSelect'
> & {
    getCreatorPoolAccounts: (creator: influencer.CreatorSearchResult) => CreatorPoolAccounts;
    poolId: number | null | undefined;
    /**
     * TODO(ROU-1264): creatorbase-ui's VirtualizedTable doesn't forward react-table's row-selection
     * state yet. Once it does, drop these two for that API.
     */
    getIsCreatorSelected: (creator: influencer.CreatorSearchResult) => boolean;
    /** False for a creator with nothing left to add, or none of the identity a selection is keyed on. */
    getIsCreatorSelectable: (creator: influencer.CreatorSearchResult) => boolean;
    onCreatorAdd: (creator: influencer.CreatorSearchResult) => void;
};

export const getTableMeta = getTableMetaHelper<Meta>();

export const getCreatorColumns = (): Column[] => [
    {
        header: 'Creator',
        id: 'creator',
        cell: CreatorCell,
        size: 200,
        enableSorting: false,
    },
    {
        header: 'Location',
        id: 'location',
        cell: LocationCell,
        enableSorting: false,
    },
    {
        header: 'Tags',
        id: 'tags',
        cell: TagsCell,
        size: 200,
        enableSorting: false,
    },
    {
        header: 'Draft Time',
        id: 'metrics.avg_draft_time_in_hours',
        meta: { className: styles.muted },
        accessorFn: (row) => getAverageDraftTimeLabel(row.metrics?.avg_draft_time_in_hours),
        cell: SkeletonCell,
        size: 110,
    },
    {
        header: 'Response',
        id: 'metrics.response_rate',
        cell: ResponseRateCell,
        size: 110,
    },
    {
        header: 'Last Collab',
        id: 'post_metrics.most_recent_post_date',
        meta: { className: styles.muted },
        accessorFn: (row) => getLastCollabLabel(row?.post_metrics?.most_recent_post_date),
        cell: SkeletonCell,
        size: 110,
    },
    {
        header: 'Platform',
        id: 'platform',
        meta: { className: styles.platformSelectorCell },
        cell: PlatformSelectorCell,
        size: 145,
        enableSorting: false,
    },
    {
        header: 'Avg. CPE',
        id: 'estimated_cpe',
        cell: AvgCpeCell,
        size: PLATFORM_STAT_COLUMN_SIZE,
    },
    {
        header: 'Followers',
        id: 'total_follower_count',
        cell: FollowersCell,
        size: PLATFORM_STAT_COLUMN_SIZE,
    },
    {
        header: 'Cost',
        id: 'cost',
        cell: CostCell,
        size: PLATFORM_STAT_COLUMN_SIZE,
    },
    {
        header: 'Plays',
        id: 'avg_video_views_per_post',
        cell: PlaysCell,
        size: PLATFORM_STAT_COLUMN_SIZE,
    },
    {
        header: 'CPM',
        id: 'estimated_cpm',
        cell: CpmCell,
        size: PLATFORM_STAT_COLUMN_SIZE,
    },
    {
        header: 'ER%',
        id: 'avg_engagement_rate',
        cell: EnagementRateCell,
        size: PLATFORM_STAT_COLUMN_SIZE,
    },
    {
        header: 'Posting Freq',
        id: 'post_metrics.activity_rate',
        size: PLATFORM_STAT_COLUMN_SIZE,
        cell: PostingFrequencyCell,
    },
    {
        header: 'Date Added',
        id: DATE_ADDED_COLUMN_ID,
        accessorFn: getDateAddedValue,
        cell: SkeletonCell,
        meta: { className: cn(styles.dateAddedCell, styles.muted) },
    },
];
